import React, { useEffect } from 'react';
import CssBaseline from '@mui/joy/CssBaseline';
import { CssVarsProvider as JoyCssVarsProvider, extendTheme as extendJoyTheme, useColorScheme as useJoyColorScheme } from '@mui/joy/styles';
import {
  createTheme,
  THEME_ID,
  ThemeProvider as MaterialThemeProvider,
  useColorScheme as useMaterialColorScheme,
} from '@mui/material/styles';
import { COLOR_MODE_STORAGE_KEY, resolveColorMode } from './colorMode';

const joyTheme = extendJoyTheme();

const materialTheme = createTheme({
  cssVariables: {
    colorSchemeSelector: '[data-joy-color-scheme="%s"]',
  },
  colorSchemes: {
    light: true,
    dark: true,
  },
  transitions: {
    duration: {
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
    },
    easing: {
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px rgba(0, 0, 0, 0.12)',
    '0px 1px 5px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px rgba(0, 0, 0, 0.12)',
    '0px 1px 8px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.14), 0px 3px 3px rgba(0, 0, 0, 0.12)',
    '0px 4px 12px rgba(0, 0, 0, 0.1)',
    '0px 8px 24px rgba(0, 0, 0, 0.2)',
    '0px 1px 3px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px rgba(0, 0, 0, 0.12)',
    '0px 1px 5px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px rgba(0, 0, 0, 0.12)',
    '0px 1px 8px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.14), 0px 3px 3px rgba(0, 0, 0, 0.12)',
    '0px 4px 12px rgba(0, 0, 0, 0.1)',
    '0px 8px 24px rgba(0, 0, 0, 0.2)',
    '0px 1px 3px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px rgba(0, 0, 0, 0.12)',
    '0px 1px 5px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px rgba(0, 0, 0, 0.12)',
    '0px 1px 8px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.14), 0px 3px 3px rgba(0, 0, 0, 0.12)',
    '0px 4px 12px rgba(0, 0, 0, 0.1)',
    '0px 8px 24px rgba(0, 0, 0, 0.2)',
    '0px 1px 3px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px rgba(0, 0, 0, 0.12)',
    '0px 1px 5px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px rgba(0, 0, 0, 0.12)',
    '0px 1px 8px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.14), 0px 3px 3px rgba(0, 0, 0, 0.12)',
  ],
  palette: {
    text: {
      secondary: '#6b7280',
      disabled: '#9ca3af',
    },
    action: {
      active: '#00000099',
      hover: '#0000000a',
      selected: '#0000001f',
      disabled: '#00000042',
      disabledBackground: '#0000001f',
      focus: '#0000001f',
    },
    error: {
      main: '#d32f2f',
    },
  },
});

const scopedMaterialTheme = {
  [THEME_ID]: materialTheme,
};

const SyncColorMode = () => {
  const { mode: joyMode, systemMode: joySystemMode } = useJoyColorScheme();
  const {
    mode: materialMode,
    systemMode: materialSystemMode,
    setMode: setMaterialMode,
  } = useMaterialColorScheme();

  useEffect(() => {
    const resolvedJoyMode = resolveColorMode(joyMode, joySystemMode);
    const resolvedMaterialMode = resolveColorMode(materialMode, materialSystemMode);

    if (!resolvedJoyMode || resolvedJoyMode === resolvedMaterialMode) {
      return;
    }

    setMaterialMode(resolvedJoyMode);
  }, [joyMode, joySystemMode, materialMode, materialSystemMode, setMaterialMode]);

  return null;
};

const AppThemeProviders = ({ children }) => (
  <MaterialThemeProvider
    theme={scopedMaterialTheme}
    defaultMode="system"
    storageWindow={null}
    disableTransitionOnChange
  >
    <JoyCssVarsProvider
      theme={joyTheme}
      defaultMode="system"
      modeStorageKey={COLOR_MODE_STORAGE_KEY}
      disableTransitionOnChange
    >
      <SyncColorMode />
      <CssBaseline />
      {children}
    </JoyCssVarsProvider>
  </MaterialThemeProvider>
);

export default AppThemeProviders;
